import { useState, useEffect, useCallback } from 'react';
import {
    X, ChevronRight, ChevronLeft,
    Home, FileText, MessageCircle, Target,
    Sparkles, Users, MoreHorizontal, BookOpen,
    Settings, Info, ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { preferencesAPI } from '@/api/preferences';
import { isAuthenticated, getPortalType } from '@/utils/auth';
import { getAnonymousUserPreferences } from '@/utils/anonymousAuth';
import { getSignedGuideUrl } from '@/services/api';

// ─── localStorage helpers (guest only) ───────────────────────────────────────
const ANON_KEY = 'anonymous_guide_seen';
const markAnonGuideSeen = () => localStorage.setItem(ANON_KEY, 'true');
const anonGuideSeen = () => localStorage.getItem(ANON_KEY) === 'true';

// ─── Screen definitions ───────────────────────────────────────────────────────
const SCREENS = [
    {
        id: 'welcome',
        badge: null,
        badgeColor: null,
        title: 'Meet Budlee AI',
        subtitle:
            'Your personal AI learning companion — understand your lessons better, build curiosity early, and become an expert in what you love.',
        media: 'welcome',
    },
    {
        id: 'learn',
        badge: 'LEARN',
        badgeColor: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
        title: 'Learn every chapter, the right way',
        subtitle:
            'Every chapter broken into bite-sized videos, concepts, practice and more.',
        media: 'video',
        videoUrl: 'settings/user_guide/video1.mp4',
    },
    {
        id: 'notes',
        badge: 'NOTES',
        badgeColor: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
        title: 'Your class notes, now smarter',
        subtitle:
            'Upload your class notes and ask Budlee anything from what was taught.',
        media: 'video',
        videoUrl: 'settings/user_guide/video2.mp4',
    },
    {
        id: 'chat',
        badge: 'CHAT',
        badgeColor: 'bg-green-500/20 text-green-400 border border-green-500/30',
        title: 'Clear any doubt in under 30 seconds',
        subtitle:
            'Ask from your notes or textbook — Budlee answers instantly with videos, images and more.',
        media: 'video',
        videoUrl: 'settings/user_guide/video3.mp4',
    },
    {
        id: 'quest',
        badge: 'QUEST',
        badgeColor: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
        title: 'Practice, get feedback, and improve',
        subtitle:
            'Write your answer, get instant feedback, and see the perfect answer explained.',
        media: 'video',
        videoUrl: 'settings/user_guide/video4.mp4',
    },
    {
        id: 'curiosity',
        badge: 'CURIOSITY',
        badgeColor: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
        title: 'Discover what you are truly curious about',
        subtitle:
            'Explore AI, Astrophysics, Robotics and more — and go deeper than any textbook ever could.',
        media: 'video',
        videoUrl: 'settings/user_guide/video5.mp4',
    },
    {
        id: 'more',
        badge: 'MORE',
        badgeColor: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
        title: 'Explore more features',
        subtitle:
            "You can find detailed guides for all features anytime — tap '…More' in the sidebar or bottom navbar, then select 'User Guide'.",
        media: 'sidebar-illustration',
    },
    {
        id: 'ready',
        badge: null,
        badgeColor: null,
        title: 'Your learning journey starts now',
        subtitle:
            'Pick your grade and subjects — Budlee does the rest. Ready to get smarter every day?',
        media: 'ready',
    },
];

// ─── Sidebar Illustration for Screen 7 ───────────────────────────────────────
function SidebarIllustration() {
    const items = [
        { icon: Settings, label: 'SETTINGS', color: 'text-gray-400', highlight: false },
        { icon: BookOpen, label: 'USER GUIDE', color: 'text-emerald-400', highlight: true },
        { icon: Info, label: 'ABOUT', color: 'text-emerald-400', highlight: false },
    ];

    return (
        <div className="w-full h-full flex items-center justify-center p-4">
            <div
                className="w-full max-w-xs rounded-xl border border-[hsl(var(--card-border))] bg-[hsl(var(--sidebar-bg))] overflow-hidden shadow-xl"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
                {/* Sidebar header mock */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-[hsl(var(--card-border))]">
                    <img src="/logo.png" alt="Budlee AI" className="h-7 w-7 object-contain" />
                    <span className="text-xs font-bold tracking-wide" style={{
                        background: 'linear-gradient(135deg, hsl(var(--teal-vivid)), hsl(var(--green-bright)))',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}>Budlee AI</span>
                </div>

                {/* MORE section label */}
                <div className="px-4 pt-3 pb-1">
                    <div className="flex items-center gap-2 text-muted-foreground text-[10px] uppercase tracking-widest font-semibold">
                        <MoreHorizontal className="w-3.5 h-3.5" />
                        <span>More</span>
                    </div>
                </div>

                {/* Items */}
                <div className="px-3 pb-3 space-y-1">
                    {items.map((item) => {
                        const Icon = item.icon;
                        return (
                            <div
                                key={item.label}
                                className={cn(
                                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium uppercase tracking-wide transition-all relative',
                                    item.highlight
                                        ? 'border border-[hsl(var(--teal-vivid))]/60 bg-[hsl(var(--teal-vivid))]/10 animate-pulse'
                                        : 'text-muted-foreground'
                                )}
                            >
                                <Icon className={cn('w-4 h-4 flex-shrink-0', item.color)} />
                                <span className={item.highlight ? 'text-emerald-300' : ''}>{item.label}</span>

                                {/* Animated cursor on User Guide */}
                                {item.highlight && (
                                    <div className="ml-auto flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-full bg-[hsl(var(--teal-vivid))] animate-ping" />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Caption */}
                <div className="px-4 pb-3">
                    <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
                        Find <span className="text-emerald-400 font-semibold">detailed feature guides</span> in <span className="text-emerald-400 font-semibold">'User Guide'</span> anytime
                    </p>
                </div>
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function UserGuideModal({ isOpen, onClose }) {
    const [currentScreen, setCurrentScreen] = useState(0);
    const [isMarkingDone, setIsMarkingDone] = useState(false);
    const [videoSrc, setVideoSrc] = useState(null);
    const [prefetchCache, setPrefetchCache] = useState({});
    const [isVideoReady, setIsVideoReady] = useState(false);
    const total = SCREENS.length;

    // Reset to first screen each time modal opens
    useEffect(() => {
        if (isOpen) setCurrentScreen(0);
    }, [isOpen]);

    // Video signing and Warming logic
    useEffect(() => {
        if (!isOpen) return;

        const screen = SCREENS[currentScreen];
        const loadVideo = async () => {
            if (screen.media !== 'video' || !screen.videoUrl) {
                setVideoSrc(null);
                setIsVideoReady(false);
                return;
            }

            // Check cache first
            if (prefetchCache[currentScreen]) {
                setVideoSrc(prefetchCache[currentScreen]);
            } else {
                const url = await getSignedGuideUrl(screen.videoUrl);
                setVideoSrc(url);
            }
            setIsVideoReady(false); // Reset ready state when source changes

            // Prefetch the next item
            const nextScreenIdx = currentScreen + 1;
            const nextScreen = SCREENS[nextScreenIdx];
            if (nextScreen?.media === 'video' && nextScreen.videoUrl && !prefetchCache[nextScreenIdx]) {
                getSignedGuideUrl(nextScreen.videoUrl).then(url => {
                    if (url) setPrefetchCache(prev => ({ ...prev, [nextScreenIdx]: url }));
                });
            }
        };

        loadVideo();
    }, [currentScreen, isOpen]);

    const markSeen = useCallback(async () => {
        if (isMarkingDone) return;
        setIsMarkingDone(true);
        const portal = getPortalType(window.location.pathname || '');
        const authed = isAuthenticated(portal);
        if (authed) {
            try {
                await preferencesAPI.updatePreferences({ has_seen_user_guide: true });
            } catch (err) {
                console.warn('[UserGuide] Failed to persist guide seen flag:', err);
            }
        } else if (getAnonymousUserPreferences()) {
            markAnonGuideSeen();
        }
    }, [isMarkingDone]);

    const handleClose = useCallback(async () => {
        await markSeen();
        onClose();
    }, [markSeen, onClose]);

    const handleNext = () => {
        if (currentScreen < total - 1) {
            setCurrentScreen((s) => s + 1);
        } else {
            handleClose();
        }
    };

    const handleBack = () => {
        if (currentScreen > 0) setCurrentScreen((s) => s - 1);
    };

    const isParentPath = typeof window !== 'undefined' && (window.location.href.toLowerCase().includes('/parent') || window.location.hash.toLowerCase().includes('/parent'));
    if (!isOpen || isParentPath) return null;

    const screen = SCREENS[currentScreen];
    const isFirst = currentScreen === 0;
    const isLast = currentScreen === total - 1;

    return (
        /* Backdrop */
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/70 backdrop-blur-sm"
            onClick={(e) => {
                if (e.target === e.currentTarget) handleClose();
            }}
        >
            {/* Card */}
            <div
                className={cn(
                    'bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-2xl shadow-2xl transition-all duration-300',
                    'flex flex-col overflow-y-auto overflow-x-hidden',
                    'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] h-auto max-h-[calc(100dvh-(env(safe-area-inset-top)+env(safe-area-inset-bottom)+9rem))]',
                    'md:max-h-[85vh] md:max-w-3xl md:w-full'
                )}
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Close Button ─────────────────────────────────────────────── */}
                <button
                    onClick={handleClose}
                    className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-[hsl(var(--card-border))]/50 hover:bg-[hsl(var(--card-border))] text-muted-foreground hover:text-foreground transition-all"
                    aria-label="Close guide"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* ── Scrollable Body ───────────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-[300px] md:min-h-[500px]">
                    {/* Welcome Screen */}
                    {screen.media === 'welcome' && (
                        <div
                            key={screen.id}
                            className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex flex-col items-center justify-center text-center px-6 py-10 sm:py-14 min-h-[300px] md:min-h-[500px]"
                            style={{
                                background:
                                    'radial-gradient(ellipse at 50% 0%, hsl(var(--green-bright)/0.15) 0%, transparent 70%), linear-gradient(160deg, hsl(var(--teal-vivid)/0.08) 0%, transparent 60%)',
                            }}
                        >
                            <img
                                src="/logo_tagline_black.png"
                                alt="Budlee AI"
                                className="dark:hidden h-14 sm:h-16 w-auto max-w-[240px] sm:max-w-[280px] object-contain mb-5"
                            />
                            <img
                                src="/logo_tagline_white.png"
                                alt="Budlee AI"
                                className="hidden dark:block h-14 sm:h-16 w-auto max-w-[240px] sm:max-w-[280px] object-contain mb-5"
                            />
                            <h1 className="budlee-brand text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">
                                {screen.title}
                            </h1>
                            <p className="text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed max-w-sm md:max-w-md lg:max-w-lg">
                                {screen.subtitle}
                            </p>
                        </div>
                    )}

                    {/* Ready (Last) Screen */}
                    {screen.media === 'ready' && (
                        <div
                            key={screen.id}
                            className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex flex-col items-center justify-center text-center px-6 py-10 sm:py-14 min-h-[300px] md:min-h-[500px]"
                            style={{
                                background:
                                    'radial-gradient(ellipse at 50% 100%, hsl(var(--teal-vivid)/0.15) 0%, transparent 70%)',
                            }}
                        >
                            <div className="mb-5 p-4 rounded-full bg-gradient-to-br from-[hsl(var(--green-bright))]/20 to-[hsl(var(--teal-vivid))]/20 border border-[hsl(var(--green-bright))]/30">
                                <img
                                    src="/logo.png"
                                    alt="Budlee AI"
                                    className="h-16 w-16 object-contain"
                                />
                            </div>
                            <h1 className="budlee-brand text-2xl sm:text-3xl font-extrabold text-foreground mb-3 leading-tight">
                                {screen.title}
                            </h1>
                            <p className="text-sm sm:text-base md:text-lg text-muted-foreground leading-relaxed max-w-sm md:max-w-md lg:max-w-lg">
                                {screen.subtitle}
                            </p>
                        </div>
                    )}

                    {/* Feature Screens (video or sidebar illustration) */}
                    {(screen.media === 'video' || screen.media === 'sidebar-illustration') && (
                        <div key={screen.id} className="animate-in fade-in slide-in-from-bottom-2 duration-300 px-4 sm:px-6 pt-6 pb-2 min-h-[300px] md:min-h-[500px]">
                            {/* Badge */}
                            {screen.badge && (
                                <span className={cn(
                                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2',
                                    screen.badgeColor
                                )}>
                                    {screen.badge}
                                </span>
                            )}

                            {/* Title & Subtitle */}
                            <h2 className="budlee-brand text-xl sm:text-2xl font-extrabold text-foreground mb-1.5 leading-tight">
                                {screen.title}
                            </h2>
                            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
                                {screen.subtitle}
                            </p>

                            {/* Media Box - Explicit 16/9 Aspect Ratio */}
                            <div 
                                className="relative aspect-video w-full bg-black rounded-2xl overflow-hidden border border-[hsl(var(--card-border))] shadow-xl group"
                                onContextMenu={(e) => e.preventDefault()}
                            >
                                {screen.media === 'video' ? (
                                    <>
                                        <video
                                            src={videoSrc}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                            controlsList="nodownload nofullscreen noremoteplayback"
                                            disablePictureInPicture
                                            onContextMenu={(e) => e.preventDefault()}
                                            onCanPlay={() => setIsVideoReady(true)}
                                            style={{ pointerEvents: 'none' }}
                                            className={cn(
                                                "w-full h-full object-contain bg-black/40 transition-opacity duration-300",
                                                isVideoReady ? "opacity-100" : "opacity-0"
                                            )}
                                        />
                                        {!isVideoReady && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 animate-pulse">
                                                <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            </div>
                                        )}
                                        {/* Hidden Preloader for next screen */}
                                        {(() => {
                                            const nextScreenIdx = currentScreen + 1;
                                            const nextUrl = prefetchCache[nextScreenIdx];
                                            return nextUrl ? (
                                                <video 
                                                    key={`preload-modal-${nextScreenIdx}`}
                                                    src={nextUrl} 
                                                    preload="auto" 
                                                    muted 
                                                    style={{ display: 'none' }} 
                                                />
                                            ) : null;
                                        })()}
                                    </>
                                ) : (
                                    <SidebarIllustration />
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Footer ───────────────────────────────────────────────────── */}
                <div className="px-4 sm:px-6 pt-3 pb-4 sm:pb-5 flex items-center justify-between gap-3 border-t border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))]">
                    {/* Progress dots */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {Array.from({ length: total }).map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentScreen(i)}
                                aria-label={`Go to screen ${i + 1}`}
                                className={cn(
                                    'rounded-full transition-all duration-300 cursor-pointer',
                                    i === currentScreen
                                        ? 'w-4 h-2 bg-[hsl(var(--green-bright))]'
                                        : i < currentScreen
                                            ? 'w-2 h-2 bg-[hsl(var(--green-bright))]/40'
                                            : 'w-2 h-2 border border-[hsl(var(--card-border))]'
                                )}
                            />
                        ))}
                    </div>

                    {/* Navigation buttons */}
                    <div className="flex items-center gap-2">


                        {/* Back */}
                        {!isFirst && (
                            <button
                                onClick={handleBack}
                                className="flex items-center gap-1 text-xs sm:text-sm px-3 py-1.5 rounded-lg border border-[hsl(var(--card-border))] text-muted-foreground hover:text-foreground hover:border-[hsl(var(--green-bright))]/50 transition-all"
                            >
                                <ChevronLeft className="w-3.5 h-3.5" />
                                <span>Back</span>
                            </button>
                        )}

                        {/* Next / Let's Go */}
                        <button
                            onClick={handleNext}
                            className={cn(
                                'flex items-center gap-1.5 text-xs sm:text-sm px-4 py-1.5 rounded-lg font-semibold transition-all',
                                isLast
                                    ? 'bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white shadow-lg hover:opacity-90 px-5'
                                    : 'bg-[hsl(var(--green-bright))]/10 text-[hsl(var(--green-bright))] border border-[hsl(var(--green-bright))]/30 hover:bg-[hsl(var(--green-bright))]/20'
                            )}
                        >
                            {isLast ? (
                                <>
                                    Let's Go
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </>
                            ) : (
                                <>
                                    Next
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
